import React, { useMemo, useEffect } from 'react';
import { MapContainer, TileLayer, CircleMarker, Tooltip, Marker, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

const REGION_COORDS: Record<string, [number, number]> = {
  'Adamaoua':     [7.3833,  13.5833],
  'Centre':       [4.5000,  11.5000],
  'Est':          [4.0000,  14.0000],
  'Extrême-Nord': [11.8333, 14.6667],
  'Littoral':     [4.0000,   9.7000],
  'Nord':         [8.3667,  13.7833],
  'Nord-Ouest':   [6.0000,  10.1667],
  'Ouest':        [5.5000,  10.4167],
  'Sud':          [3.0000,  11.5000],
  'Sud-Ouest':    [4.5000,   9.3333],
};

interface Agent {
  region: string; attendance_rate: number; score_attrition: number;
  risque_attrition: string; statut: string;
}
interface Props {
  agents: Agent[]; selected: string | null; onSelect: (r: string) => void;
}

const getColor = (p: number) => p>=85?"#10b981":p>=70?"#3b82f6":p>=55?"#f97316":"#ef4444";
const getRisk  = (r: number) => r>70?"#ef4444":r>50?"#f97316":r>30?"#eab308":"#10b981";

const Resizer = () => {
  const map = useMap();
  useEffect(() => { setTimeout(() => map.invalidateSize(), 300); }, [map]);
  return null;
};

export const CameroonLeafletMap: React.FC<Props> = ({ agents, selected, onSelect }) => {
  const stats = useMemo(() => {
    const m: Record<string,{count:number;pres:number;attr:number;crit:number;elev:number;actifs:number}> = {};
    agents.forEach(a => {
      const k = a.region;
      if (!m[k]) m[k]={count:0,pres:0,attr:0,crit:0,elev:0,actifs:0};
      m[k].count++;
      m[k].pres += Number(a.attendance_rate)||0;
      m[k].attr += Number(a.score_attrition)||0;
      if (a.risque_attrition==="CRITIQUE") m[k].crit++;
      if (a.risque_attrition==="ELEVE")    m[k].elev++;
      if (a.statut==="Actif")              m[k].actifs++;
    });
    return Object.entries(m).map(([region,d])=>({
      region, count:d.count,
      presence:  Math.round(d.pres/d.count),
      attrition: Math.round(d.attr/d.count),
      crit:d.crit, elev:d.elev, actifs:d.actifs,
    }));
  }, [agents]);

  const maxCount = Math.max(...stats.map(r=>r.count),1);

  return (
    <div className="relative w-full rounded-xl overflow-hidden border border-blue-900/40" style={{height:"420px"}}>
      <style>{`
        .nx-tip .leaflet-tooltip{background:transparent!important;border:none!important;box-shadow:none!important;padding:0!important}
        .nx-tip .leaflet-tooltip::before{display:none!important}
        .leaflet-container{background:#0f172a!important}
        .leaflet-control-zoom a{background:#1e293b!important;color:#94a3b8!important;border-color:#334155!important}
        .leaflet-control-zoom a:hover{background:#334155!important;color:white!important}
        .leaflet-control-attribution{background:rgba(15,23,42,.8)!important;color:#475569!important;font-size:9px!important}
        @keyframes markerPulse {
          0%   { transform: scale(1);   opacity: .7; }
          50%  { transform: scale(1.15); opacity: 1; }
          100% { transform: scale(1);   opacity: .7; }
        }
        .pulse-marker { animation: markerPulse 2.2s ease-in-out infinite; }
        .pulse-marker-sel { animation: markerPulse 1s ease-in-out infinite; }
      `}</style>

      <MapContainer center={[5.5,12.3]} zoom={6}
        style={{width:"100%",height:"100%",background:"#0f172a"}}
        attributionControl={false} zoomControl scrollWheelZoom>
        <Resizer/>
        <TileLayer
          url="https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png"
          subdomains="abcd" maxZoom={19}/>

        {stats.map(({region,count,presence,attrition,crit,elev,actifs})=>{
          const coords = REGION_COORDS[region];
          if (!coords) return null;
          const color  = getColor(presence);
          const rColor = getRisk(attrition);
          const isSel  = selected===region;
          const radius = Math.max(16,Math.min(36,16+(count/maxCount)*20));

          // Label icon with region name + count
          const labelIcon = L.divIcon({
            className: "",
            html: `<div class="${isSel?"pulse-marker-sel":"pulse-marker"}" style="
              display:flex;flex-direction:column;align-items:center;
              pointer-events:none;
            ">
              <div style="
                background:${color}22;border:${isSel?"3px solid #fff":"2px solid "+color};
                border-radius:50%;width:${radius*2}px;height:${radius*2}px;
                display:flex;flex-direction:column;align-items:center;justify-content:center;
                box-shadow:0 0 ${isSel?18:10}px ${color}88;
              ">
                <span style="color:white;font-size:${radius>24?12:10}px;font-weight:700;line-height:1">${count}</span>
                <span style="color:${color};font-size:8px;font-weight:600">${presence}%</span>
              </div>
              <div style="
                margin-top:2px;background:#0f172acc;border:1px solid ${color}66;
                border-radius:4px;padding:1px 5px;
                color:${isSel?"white":color};font-size:9px;font-weight:${isSel?700:500};
                white-space:nowrap;
              ">${region}</div>
            </div>`,
            iconSize:   [radius*2+4, radius*2+24],
            iconAnchor: [radius+2,   radius+12],
          });

          return (
            <React.Fragment key={region}>
              {/* Animated marker with label */}
              <Marker position={coords} icon={labelIcon}
                eventHandlers={{click:()=>onSelect(region)}}>
                <Tooltip className="nx-tip" direction="top" offset={[0,-(radius+28)]} opacity={1}>
                  <div style={{
                    background:"#0f172a",border:"1px solid "+color,borderRadius:"10px",
                    padding:"10px 12px",fontFamily:"system-ui,sans-serif",color:"white",
                    minWidth:"170px",boxShadow:"0 4px 20px "+color+"44"
                  }}>
                    <div style={{fontSize:"12px",fontWeight:700,color:color,marginBottom:"6px"}}>
                      📍 {region}
                    </div>
                    <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:"4px"}}>
                      {[
                        {v:count,        l:"Agents",   c:color},
                        {v:presence+"%", l:"Présence", c:color},
                        {v:attrition,    l:"Attrition",c:rColor},
                        {v:actifs,       l:"Actifs",   c:"#10b981"},
                      ].map((item,i)=>(
                        <div key={i} style={{background:"#1e293b",borderRadius:"5px",padding:"4px",textAlign:"center"}}>
                          <div style={{fontSize:"13px",fontWeight:"bold",color:item.c}}>{item.v}</div>
                          <div style={{fontSize:"8px",color:"#94a3b8"}}>{item.l}</div>
                        </div>
                      ))}
                    </div>
                    {crit>0&&<div style={{marginTop:"5px",background:"#ef444422",border:"1px solid #ef4444",borderRadius:"4px",padding:"2px 6px",fontSize:"9px",color:"#ef4444"}}>⚠ {crit} CRITIQUE</div>}
                    {elev>0&&<div style={{marginTop:"3px",background:"#f9731622",border:"1px solid #f97316",borderRadius:"4px",padding:"2px 6px",fontSize:"9px",color:"#f97316"}}>⚠ {elev} ÉLEVÉ</div>}
                    <div style={{marginTop:"6px",fontSize:"9px",color:"#3b82f6",textAlign:"center"}}>
                      {isSel?"✓ Actif — cliquer pour retirer":"Cliquer pour filtrer →"}
                    </div>
                  </div>
                </Tooltip>
              </Marker>

              {/* Invisible CircleMarker for accurate click area */}
              <CircleMarker center={coords} radius={radius}
                pathOptions={{fillOpacity:0,opacity:0}}
                eventHandlers={{click:()=>onSelect(region)}}/>
            </React.Fragment>
          );
        })}
      </MapContainer>

      {/* Legend */}
      <div className="absolute bottom-3 left-3 bg-slate-900/90 border border-blue-900/40 rounded-lg p-2 pointer-events-none" style={{zIndex:1000}}>
        <p className="text-slate-300 text-xs font-semibold mb-1.5">Taux de présence</p>
        {[{c:"#10b981",l:"≥ 85%"},{c:"#3b82f6",l:"70–84%"},{c:"#f97316",l:"55–69%"},{c:"#ef4444",l:"< 55%"}].map((item,i)=>(
          <div key={i} className="flex items-center gap-1.5 mb-0.5">
            <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{background:item.c}}/>
            <span className="text-slate-400" style={{fontSize:"9px"}}>{item.l}</span>
          </div>
        ))}
      </div>

      {/* Selected badge */}
      {selected && (
        <div className="absolute top-3 right-3 bg-blue-500/20 border border-blue-500/50 rounded-lg px-3 py-1.5 pointer-events-none" style={{zIndex:1000}}>
          <span className="text-blue-300 text-xs font-semibold">📍 {selected}</span>
        </div>
      )}
    </div>
  );
};
